<template>
  <ion-page>
    <PageHeaderWithMenu title="Тестирование" :scrolled="headerScrolled" />
    <ion-content
      :class="[isMobile ? 'main-background' : 'liga-web-background']"
      :fullscreen="true"
      @ionScroll="onContentScroll">
      <ion-refresher slot="fixed" @ionRefresh="loadTests($event)">
        <ion-refresher-content refreshingSpinner="bubbles"></ion-refresher-content>
      </ion-refresher>

      <ion-card v-show="showCreateForm" class="admin-card">
        <ion-card-header>
          <ion-card-title>Создать тест</ion-card-title>
          <ion-buttons slot="end">
            <ion-button fill="clear" size="small" class="admin-card-btn" @click="showCreateForm = false">Свернуть</ion-button>
          </ion-buttons>
        </ion-card-header>
        <ion-card-content>
          <ion-item>
            <ion-input label="Название" label-placement="stacked" v-model="newTest.name" placeholder="Название теста"></ion-input>
          </ion-item>
          <ion-item>
            <ion-input label="Описание (необязательно)" label-placement="stacked" v-model="newTest.description" placeholder="Описание"></ion-input>
          </ion-item>
          <ion-item>
            <ion-input label="Порог успешности (%)" label-placement="stacked" type="number" v-model.number="newTest.passingThreshold" min="0" max="100"></ion-input>
          </ion-item>
          <ion-item>
            <ion-input label="Макс. попыток (пусто = без ограничения)" label-placement="stacked" type="number" v-model.number="newTest.maxAttempts" placeholder="Пусто"></ion-input>
          </ion-item>
          <ion-item>
            <ion-input label="Время на прохождение (ЧЧ:ММ, пусто = без ограничения)" label-placement="stacked" :model-value="(newTest.durationForm as unknown as number)" @update:model-value="(v: number) => onNewTestDurationInput(v)" placeholder="00:00" :maxlength="5"></ion-input>
          </ion-item>
          <ion-item>
            <ion-select label="Роли при успешном прохождении" label-placement="stacked" v-model="newTest.roleNames" multiple interface="popover" mode="ios" placeholder="Выберите роли">
              <ion-select-option v-for="a in allAuthorities" :key="a.name" :value="a.name">{{ a.description || a.name }}</ion-select-option>
            </ion-select>
          </ion-item>
          <ion-button expand="block" class="admin-primary-btn" @click="createTest()" :disabled="!newTest.name?.trim() || creating">
            {{ creating ? 'Создание…' : 'Создать тест' }}
          </ion-button>
          <ion-button expand="block" fill="outline" size="small" class="admin-outline-btn" @click="showCreateForm = false">Отмена</ion-button>
        </ion-card-content>
      </ion-card>

      <ion-card class="admin-card">
        <ion-card-header>
          <ion-card-title>Список тестов</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          <ion-button v-if="!showCreateForm" expand="block" class="admin-primary-btn" @click="showCreateForm = true">+ Создать тест</ion-button>
          <p v-if="loading" class="admin-hint">Загрузка…</p>
          <p v-else-if="!tests.length" class="admin-hint">Тестов пока нет.</p>
          <div v-else class="admin-tests-list">
            <div v-for="t in tests" :key="t.id" class="admin-test-row">
              <div class="admin-test-main">
                <ion-label>
                  <h2>{{ t.name }}</h2>
                  <p>Порог: {{ t.passingThreshold }}% · Попыток: {{ t.maxAttempts ?? '∞' }} · Время: {{ formatDurationDisplay(t.durationMinutes) }}</p>
                </ion-label>
                <ion-button fill="clear" size="small" class="admin-card-btn" @click="openEditModal(t)">
                  <ion-icon :icon="pencilOutline"></ion-icon>
                  Редактировать
                </ion-button>
                <ion-button fill="clear" size="small" class="admin-card-btn" @click="openQuestionsModal(t)">
                  Вопросы
                </ion-button>
                <ion-button fill="clear" size="small" class="admin-card-btn" @click="toggleAssignments(t)">
                  <ion-icon :icon="peopleOutline"></ion-icon>
                  Назначения
                </ion-button>
                <ion-button fill="clear" color="danger" size="small" class="admin-card-btn" @click="deleteTestConfirm(t)">
                  <ion-icon :icon="trashOutline"></ion-icon>
                </ion-button>
              </div>
              <div v-show="expandedTestId === t.id" class="admin-test-assignments">
                <p class="admin-assignments-title">Кому назначен тест</p>
                <ion-searchbar v-if="currentAssignments.length" v-model="assignmentsFilterQuery" placeholder="Поиск по имени или логину" :debounce="200" class="admin-assignments-search"></ion-searchbar>
                <div v-if="assignmentsLoading" key="assign-loading" class="admin-hint">Загрузка…</div>
                <ion-list v-else-if="filteredAssignments.length" key="assign-list" lines="full" class="admin-assignments-list">
                  <ion-item v-for="a in filteredAssignments" :key="'a-' + a.id + '-' + a.userId" class="admin-assignment-item">
                    <ion-label>
                      <h3>{{ assignmentDisplayName(a) }}</h3>
                      <p class="admin-assignment-login">{{ a.login }}</p>
                      <div class="admin-assignment-status-row">
                        <span v-if="a.passed" class="admin-assignment-status admin-assignment-status--passed">
                          <ion-icon :icon="checkmarkCircle"></ion-icon> Пройден
                        </span>
                        <span v-else-if="a.hasDraft" class="admin-assignment-status admin-assignment-status--draft">
                          <ion-icon :icon="createOutline"></ion-icon> В процессе
                        </span>
                        <span v-else-if="a.usedAttempts > 0" class="admin-assignment-status admin-assignment-status--failed">
                          Не пройден
                        </span>
                        <span v-else class="admin-assignment-status admin-assignment-status--none">
                          Не начат
                        </span>
                        <span class="admin-assignment-attempts">Попыток: {{ a.usedAttempts }}</span>
                        <span v-if="a.lastScore != null" class="admin-assignment-score">Результат: {{ a.lastScore }}%</span>
                      </div>
                      <div class="admin-assignment-actions">
                        <ion-button fill="clear" size="small" class="admin-card-btn" @click="resetAttempts(t.id, a.userId)">Сбросить попытки</ion-button>
                        <ion-button fill="clear" color="danger" size="small" class="admin-card-btn" @click="removeAssignment(t.id, a.userId)">
                          <ion-icon :icon="trashOutline"></ion-icon>
                        </ion-button>
                      </div>
                    </ion-label>
                  </ion-item>
                </ion-list>
                <p v-else-if="currentAssignments.length && !filteredAssignments.length" key="assign-filter-empty" class="admin-hint">Ничего не найдено.</p>
                <p v-else key="assign-none" class="admin-hint">Никому не назначен.</p>
                <ion-button expand="block" fill="outline" size="small" class="admin-outline-btn" @click="openAddUsersModal(t)">Добавить пользователей</ion-button>
              </div>
            </div>
          </div>
        </ion-card-content>
      </ion-card>
    </ion-content>

    <!-- Модальное окно выбора пользователей -->
    <ion-modal :is-open="showAddUsersModal" @didDismiss="closeAddUsersModal" class="admin-users-modal">
      <ion-header>
        <ion-toolbar>
          <ion-title>Добавить пользователей</ion-title>
          <ion-buttons slot="end">
            <ion-button class="admin-card-btn" @click="closeAddUsersModal">
              <ion-icon :icon="closeOutline"></ion-icon>
            </ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding">
        <p v-if="addUsersModalLoading" class="admin-hint">Загрузка списка пользователей…</p>
        <p v-else-if="!availableUsers.length" class="admin-hint">Нет пользователей для добавления (все уже назначены).</p>
        <template v-else>
          <ion-searchbar v-model="addUsersFilterQuery" placeholder="Поиск по имени, фамилии или логину" :debounce="200" class="admin-add-users-search"></ion-searchbar>
          <ion-list lines="full">
            <ion-item v-for="u in filteredAvailableUsers" :key="u.id" class="admin-user-pick-item">
              <ion-checkbox slot="start" :checked="selectedUserIds.includes(u.id)" @ionChange="toggleUserPick(u.id)"></ion-checkbox>
              <ion-label>
                <h3>{{ [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.login }}</h3>
                <p class="admin-user-pick-login">{{ u.login }}</p>
              </ion-label>
            </ion-item>
          </ion-list>
          <p v-if="availableUsers.length && !filteredAvailableUsers.length" class="admin-hint">Ничего не найдено.</p>
        </template>
      </ion-content>
      <ion-footer v-if="!addUsersModalLoading && availableUsers.length" class="admin-users-modal-footer">
        <ion-toolbar>
          <div class="admin-modal-actions">
            <ion-button expand="block" class="admin-primary-btn" @click="confirmAddUsers" :disabled="!selectedUserIds.length || addingUsers">
              {{ addingUsers ? 'Добавление…' : 'Добавить выбранных' }}
            </ion-button>
            <ion-button expand="block" fill="outline" class="admin-outline-btn" @click="closeAddUsersModal">Отмена</ion-button>
          </div>
        </ion-toolbar>
      </ion-footer>
    </ion-modal>

    <!-- Модальное окно редактирования теста -->
    <ion-modal :is-open="showEditModal" @didDismiss="closeEditModal" class="admin-edit-test-modal">
      <ion-header>
        <ion-toolbar>
          <ion-title>Редактировать тест</ion-title>
          <ion-buttons slot="end">
            <ion-button class="admin-card-btn" @click="closeEditModal">
              <ion-icon :icon="closeOutline"></ion-icon>
            </ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding">
        <p v-if="editLoading" class="admin-hint">Загрузка…</p>
        <template v-else>
          <ion-item>
            <ion-input label="Название" label-placement="stacked" v-model="editTest.name" placeholder="Название теста"></ion-input>
          </ion-item>
          <ion-item>
            <ion-input label="Описание (необязательно)" label-placement="stacked" v-model="editTest.description" placeholder="Описание"></ion-input>
          </ion-item>
          <ion-item>
            <ion-input label="Порог успешности (%)" label-placement="stacked" type="number" v-model.number="editTest.passingThreshold" min="0" max="100"></ion-input>
          </ion-item>
          <ion-item>
            <ion-input label="Макс. попыток (пусто = без ограничения)" label-placement="stacked" type="number" v-model.number="editTest.maxAttempts" placeholder="Пусто"></ion-input>
          </ion-item>
          <ion-item>
            <ion-input label="Время на прохождение (ЧЧ:ММ, пусто = без ограничения)" label-placement="stacked" :model-value="(editTest.durationForm as unknown as number)" @update:model-value="(v: number) => onEditTestDurationInput(v)" placeholder="00:00" :maxlength="5"></ion-input>
          </ion-item>
          <ion-item>
            <ion-select :key="'edit-roles-' + editTestId + '-' + (editTest.roleNames || []).join(',')" label="Роли при успешном прохождении" label-placement="stacked" v-model="editTest.roleNames" multiple interface="popover" mode="ios" placeholder="Выберите роли">
              <ion-select-option v-for="a in allAuthorities" :key="a.name" :value="a.name">{{ a.description || a.name }}</ion-select-option>
            </ion-select>
          </ion-item>
          <div class="admin-modal-actions">
            <ion-button expand="block" class="admin-primary-btn" @click="saveEditTest()" :disabled="!editTest.name?.trim() || saving">
              {{ saving ? 'Сохранение…' : 'Сохранить' }}
            </ion-button>
            <ion-button expand="block" fill="outline" class="admin-outline-btn" @click="closeEditModal">Отмена</ion-button>
          </div>
        </template>
      </ion-content>
    </ion-modal>

    <!-- Модальное окно вопросов и ответов теста -->
    <ion-modal :is-open="showQuestionsModal" @didDismiss="closeQuestionsModal" class="admin-questions-modal">
      <ion-header>
        <ion-toolbar>
          <ion-title>Вопросы и ответы{{ questionsTestName ? ': ' + questionsTestName : '' }}</ion-title>
          <ion-buttons slot="end">
            <ion-button class="admin-card-btn" @click="closeQuestionsModal">
              <ion-icon :icon="closeOutline"></ion-icon>
            </ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding admin-questions-content">
        <p v-if="questionsLoading" class="admin-hint">Загрузка…</p>
        <template v-else>
          <div v-if="showAddQuestionForm" class="admin-question-form admin-card">
            <p class="admin-assignments-title">Новый вопрос</p>
            <ion-item>
              <ion-input label="Текст вопроса" label-placement="stacked" v-model="newQuestion.text" placeholder="Введите вопрос"></ion-input>
            </ion-item>
            <ion-item>
              <ion-checkbox labelPlacement="end" v-model="newQuestion.multipleChoice">Несколько правильных ответов</ion-checkbox>
            </ion-item>
            <ion-item>
              <ion-input label="Порядок (число)" label-placement="stacked" type="number" v-model.number="newQuestion.sortOrder" placeholder="0"></ion-input>
            </ion-item>
            <div class="admin-modal-actions">
              <ion-button size="small" class="admin-primary-btn" @click="saveNewQuestion()" :disabled="!newQuestion.text?.trim() || saving">Сохранить вопрос</ion-button>
              <ion-button size="small" fill="outline" class="admin-outline-btn" @click="cancelAddQuestion()">Отмена</ion-button>
            </div>
          </div>
          <template v-else>
            <ion-button expand="block" size="small" class="admin-primary-btn" @click="showAddQuestionForm = true">+ Добавить вопрос</ion-button>
          </template>

          <div v-if="questionsList.length" class="admin-questions-list">
            <div v-for="q in questionsList" :key="q.id" class="admin-question-block">
              <div class="admin-question-header">
                <div class="admin-question-title">
                  <strong>{{ q.text || '(без текста)' }}</strong>
                  <span v-if="q.multipleChoice" class="admin-badge">несколько</span>
                  <span class="admin-question-order">порядок: {{ q.sortOrder ?? 0 }}</span>
                </div>
                <div class="admin-question-actions">
                  <ion-button fill="clear" size="small" class="admin-card-btn" @click="startEditQuestion(q)">Изменить</ion-button>
                  <ion-button fill="clear" color="danger" size="small" class="admin-card-btn" @click="deleteQuestionConfirm(q)">Удалить</ion-button>
                </div>
              </div>
              <div v-if="editQuestionId === q.id" class="admin-question-form admin-edit-inline">
                <ion-item>
                  <ion-input label="Текст вопроса" label-placement="stacked" v-model="editQuestionForm.text" placeholder="Введите вопрос"></ion-input>
                </ion-item>
                <ion-item>
                  <ion-checkbox labelPlacement="end" v-model="editQuestionForm.multipleChoice">Несколько правильных ответов</ion-checkbox>
                </ion-item>
                <ion-item>
                  <ion-input label="Порядок" label-placement="stacked" type="number" v-model.number="editQuestionForm.sortOrder"></ion-input>
                </ion-item>
                <ion-button size="small" class="admin-primary-btn" @click="saveEditQuestion()" :disabled="saving">Сохранить</ion-button>
                <ion-button size="small" fill="outline" class="admin-outline-btn" @click="editQuestionId = null">Отмена</ion-button>
              </div>
              <ul class="admin-answers-list">
                <li v-for="ans in (q.answers || [])" :key="ans.id" class="admin-answer-row">
                  <span class="admin-answer-text">{{ ans.text }}</span>
                  <span v-if="ans.isCorrect" class="admin-badge correct">верный</span>
                  <template v-if="editAnswerId === ans.id">
                    <ion-input v-model="editAnswerForm.text" placeholder="Текст ответа" class="admin-answer-input"></ion-input>
                    <ion-checkbox v-model="editAnswerForm.isCorrect" labelPlacement="end" class="admin-answer-checkbox">Верный</ion-checkbox>
                    <ion-button size="small" class="admin-primary-btn" @click="saveEditAnswer()" :disabled="saving">Сохранить</ion-button>
                    <ion-button size="small" fill="outline" class="admin-outline-btn" @click="editAnswerId = null">Отмена</ion-button>
                  </template>
                  <template v-else>
                    <ion-button fill="clear" size="small" class="admin-card-btn" @click="startEditAnswer(ans)">Изменить</ion-button>
                    <ion-button fill="clear" color="danger" size="small" class="admin-card-btn" @click="deleteAnswerConfirm(ans)">Удалить</ion-button>
                  </template>
                </li>
                <li v-if="addAnswerForQid === q.id" class="admin-answer-row admin-add-answer">
                  <ion-input v-model="newAnswer.text" placeholder="Текст ответа" class="admin-answer-input"></ion-input>
                  <ion-checkbox v-model="newAnswer.isCorrect" labelPlacement="end">Верный</ion-checkbox>
                  <ion-button size="small" class="admin-primary-btn" @click="saveNewAnswer(q.id)" :disabled="!newAnswer.text?.trim() || saving">Добавить</ion-button>
                  <ion-button size="small" fill="outline" class="admin-outline-btn" @click="cancelAddAnswer()">Отмена</ion-button>
                </li>
              </ul>
              <ion-button v-if="addAnswerForQid !== q.id" size="small" fill="outline" class="admin-outline-btn admin-add-answer-btn" @click="startAddAnswer(q)">+ Добавить ответ</ion-button>
            </div>
          </div>
          <p v-else-if="!showAddQuestionForm" class="admin-hint">Вопросов пока нет. Добавьте первый вопрос.</p>
        </template>
      </ion-content>
    </ion-modal>
  </ion-page>
</template>

<script lang="ts">
import { Authority } from '@/model/authority.model';
import { GlobalContext } from '@/model/globalcontext.model';
import type {
  IAdminAnswer,
  IAdminQuestion,
  IAdminTest,
  IUserTestAssignmentVM,
} from '@/services/admin.service';
import AdminService from '@/services/admin.service';
import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonCheckbox,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToolbar,
  alertController,
  toastController,
} from '@ionic/vue';
import { checkmarkCircle, closeOutline, createOutline, peopleOutline, pencilOutline, trashOutline } from 'ionicons/icons';
import PageHeaderWithMenu from '@/components/PageHeaderWithMenu.vue';
import { defineComponent, inject } from 'vue';
import axios from 'axios';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const API = (() => {
  const v = process.env.VUE_APP_API_URL || '';
  return v && !v.endsWith('/') ? v + '/' : v;
})();

async function getToken(): Promise<string> {
  const { value } = await Preferences.get({ key: 'jwt' });
  return value || '';
}

function isMobile(): boolean {
  return Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android';
}

interface TestFormVM {
  name: string;
  description: string;
  passingThreshold: number;
  maxAttempts: number | undefined;
  durationForm: string;
  roleNames: string[];
}

export default defineComponent({
  name: 'AdminTests',
  components: {
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonCheckbox,
    IonContent,
    IonFooter,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonPage,
    IonRefresher,
    IonRefresherContent,
    IonSearchbar,
    IonSelect,
    IonSelectOption,
    IonTitle,
    IonToolbar,
    PageHeaderWithMenu,
  },
  setup() {
    const globalContext = inject('globalContext') as GlobalContext;
    function assignmentDisplayName(a: IUserTestAssignmentVM): string {
      const parts = [a.firstName, a.lastName].filter(Boolean);
      return parts.length ? parts.join(' ').trim() : a.login;
    }
    return { isMobile: globalContext.isMobile, checkmarkCircle, closeOutline, createOutline, peopleOutline, pencilOutline, trashOutline, assignmentDisplayName };
  },
  data() {
    return {
      adminService: new AdminService(),
      headerScrolled: false,
      showCreateForm: false,
      loading: false,
      tests: [] as IAdminTest[],
      newTest: {
        name: '',
        description: '',
        passingThreshold: 80,
        maxAttempts: undefined as number | undefined,
        durationForm: '',
        roleNames: [] as string[],
      } as TestFormVM,
      creating: false,
      allAuthorities: [] as Authority[],
      expandedTestId: null as number | null,
      currentAssignments: [] as IUserTestAssignmentVM[],
      assignmentsLoading: false,
      showAddUsersModal: false,
      addUsersModalLoading: false,
      addUsersTestId: null as number | null,
      availableUsers: [] as { id: number; login: string; firstName?: string; lastName?: string }[],
      selectedUserIds: [] as number[],
      addingUsers: false,
      showEditModal: false,
      editTestId: null as number | null,
      editTest: {
        name: '',
        description: '',
        passingThreshold: 80,
        maxAttempts: undefined as number | undefined,
        durationForm: '',
        roleNames: [] as string[],
      } as TestFormVM,
      editLoading: false,
      saving: false,
      showQuestionsModal: false,
      questionsTestId: null as number | null,
      questionsTestName: '' as string,
      questionsList: [] as IAdminQuestion[],
      questionsLoading: false,
      showAddQuestionForm: false,
      newQuestion: { text: '', multipleChoice: false, sortOrder: 0 },
      addAnswerForQid: null as number | null,
      newAnswer: { text: '', isCorrect: false },
      editQuestionId: null as number | null,
      editQuestionForm: { text: '', multipleChoice: false, sortOrder: 0 },
      editAnswerId: null as number | null,
      editAnswerForm: { text: '', isCorrect: false },
      assignmentsFilterQuery: '',
      addUsersFilterQuery: '',
    };
  },
  computed: {
    filteredAvailableUsers(): { id: number; login: string; firstName?: string; lastName?: string }[] {
      const q = (this.addUsersFilterQuery || '').trim().toLowerCase();
      if (!q) return this.availableUsers;
      return this.availableUsers.filter((u) => {
        const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim().toLowerCase();
        const login = (u.login || '').toLowerCase();
        return name.includes(q) || login.includes(q);
      });
    },
    filteredAssignments(): IUserTestAssignmentVM[] {
      const q = (this.assignmentsFilterQuery || '').trim().toLowerCase();
      if (!q) return this.currentAssignments;
      return this.currentAssignments.filter((a) => {
        const name = ([a.firstName, a.lastName].filter(Boolean).join(' ').trim() || a.login).toLowerCase();
        const login = (a.login || '').toLowerCase();
        return name.includes(q) || login.includes(q);
      });
    },
  },
  async ionViewWillEnter() {
    this.allAuthorities = await this.adminService.getAuthoritiesFull();
    await this.loadTests();
  },
  methods: {
    onContentScroll(e: Event) {
      const ev = e as CustomEvent;
      this.headerScrolled = (ev.detail?.scrollTop ?? 0) > 30;
    },
    async loadTests(event?: CustomEvent) {
      this.loading = true;
      const expandedId = this.expandedTestId;
      try {
        this.tests = await this.adminService.getAdminTests();
        if (expandedId != null) {
          this.currentAssignments = await this.adminService.getTestAssignments(expandedId);
        }
      } catch (e) {
        console.error('loadTests', e);
        const toast = await toastController.create({
          message: 'Ошибка загрузки тестов',
          duration: 3000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.loading = false;
        (event as CustomEvent & { target?: { complete?: () => void } })?.target?.complete?.();
      }
    },
    async createTest() {
      const name = this.newTest.name?.trim();
      if (!name) return;
      this.creating = true;
      try {
        await this.adminService.createTest({
          name,
          description: this.newTest.description?.trim() || undefined,
          passingThreshold: this.newTest.passingThreshold ?? 80,
          maxAttempts: this.newTest.maxAttempts ?? undefined,
          roleNames: this.newTest.roleNames ?? [],
        });
        this.newTest.name = '';
        this.newTest.description = '';
        this.newTest.passingThreshold = 80;
        this.newTest.maxAttempts = undefined;
        this.newTest.roleNames = [];
        this.showCreateForm = false;
        const toast = await toastController.create({
          message: 'Тест создан',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
        await this.loadTests();
      } catch (e) {
        console.error('createTest', e);
        const toast = await toastController.create({
          message: 'Ошибка создания теста',
          duration: 3000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.creating = false;
      }
    },
    async toggleAssignments(t: IAdminTest) {
      if (this.expandedTestId === t.id) {
        this.expandedTestId = null;
        this.currentAssignments = [];
        return;
      }
      this.expandedTestId = t.id;
      this.currentAssignments = [];
      this.assignmentsLoading = true;
      try {
        this.currentAssignments = await this.adminService.getTestAssignments(t.id);
      } catch (e) {
        console.error('getTestAssignments', e);
      } finally {
        this.assignmentsLoading = false;
      }
    },
    async removeAssignment(testId: number, userId: number) {
      try {
        await this.adminService.removeTestAssignment(testId, userId);
        this.currentAssignments = this.currentAssignments.filter((a) => a.userId !== userId);
        const toast = await toastController.create({
          message: 'Назначение снято',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
      } catch (e) {
        console.error('removeAssignment', e);
        const toast = await toastController.create({
          message: 'Ошибка',
          duration: 2000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      }
    },
    async resetAttempts(testId: number, userId: number) {
      try {
        await this.adminService.resetTestAttempts(testId, userId);
        const toast = await toastController.create({
          message: 'Попытки сброшены',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
      } catch (e) {
        console.error('resetAttempts', e);
        const toast = await toastController.create({
          message: 'Ошибка',
          duration: 2000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      }
    },
    openAddUsersModal(t: IAdminTest) {
      this.addUsersTestId = t.id;
      this.showAddUsersModal = true;
      this.selectedUserIds = [];
      this.availableUsers = [];
      this.addUsersModalLoading = true;
      this.loadAvailableUsers();
    },
    async loadAvailableUsers() {
      if (this.addUsersTestId == null) return;
      const testId = this.addUsersTestId;
      try {
        const [assignments, raw] = await Promise.all([
          this.adminService.getTestAssignments(testId),
          (async () => {
            if (isMobile()) {
              const token = await getToken();
              const res = await CapacitorHttp.get({
                url: `${API}api/admin/users?page=0&size=500`,
                headers: { Authorization: `Bearer ${token}` },
                readTimeout: 60000,
              });
              return (res.data || []).map((u: { id: number; login: string; firstName?: string; lastName?: string }) => ({
                id: u.id,
                login: u.login,
                firstName: u.firstName,
                lastName: u.lastName,
              }));
            }
            const res = await axios.get<{ id: number; login: string; firstName?: string; lastName?: string }[]>(
              `${API}api/admin/users?page=0&size=500`
            );
            return Array.isArray(res.data) ? res.data : [];
          })(),
        ]);
        if (this.addUsersTestId !== testId) return;
        const assignedIds = new Set(assignments.map((a) => a.userId));
        this.availableUsers = raw.filter((u: { id: number; login: string; firstName?: string; lastName?: string }) => !assignedIds.has(u.id));
      } catch (e) {
        console.error('fetch users', e);
        const toast = await toastController.create({
          message: 'Ошибка загрузки пользователей',
          duration: 2000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
        this.closeAddUsersModal();
      } finally {
        this.addUsersModalLoading = false;
      }
    },
    closeAddUsersModal() {
      this.showAddUsersModal = false;
      this.addUsersTestId = null;
      this.availableUsers = [];
      this.selectedUserIds = [];
      this.addUsersFilterQuery = '';
    },
    async openEditModal(t: IAdminTest) {
      this.editTestId = t.id;
      this.showEditModal = true;
      this.editLoading = true;
      this.editTest = {
        name: '',
        description: '',
        passingThreshold: 80,
        maxAttempts: undefined as number | undefined,
        durationForm: '',
        roleNames: [] as string[],
      };
      try {
        const test = await this.adminService.getAdminTest(t.id);
        if (test) {
          const roleNamesRaw = test.roleNames ?? (test as { role_names?: string[] }).role_names ?? (test.rolesToGrant ?? []).map((r) => r.name);
          const roleNames = Array.isArray(roleNamesRaw) ? [...roleNamesRaw] : [];
          this.editTest = {
            name: test.name,
            description: test.description ?? '',
            passingThreshold: test.passingThreshold ?? 80,
            maxAttempts: test.maxAttempts ?? undefined,
            durationForm: this.durationMinutesToForm(test.durationMinutes),
            roleNames: [],
          };
          this.$nextTick(() => {
            this.editTest.roleNames = roleNames;
          });
        } else {
          const toast = await toastController.create({
            message: 'Тест не найден',
            duration: 2000,
            position: 'top',
            color: 'warning',
          });
          await toast.present();
          this.closeEditModal();
        }
      } catch (e) {
        console.error('getAdminTest', e);
        const toast = await toastController.create({
          message: 'Ошибка загрузки теста',
          duration: 2000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
        this.closeEditModal();
      } finally {
        this.editLoading = false;
      }
    },
    closeEditModal() {
      this.showEditModal = false;
      this.editTestId = null;
      this.editTest = {
        name: '',
        description: '',
        passingThreshold: 80,
        maxAttempts: undefined as number | undefined,
        durationForm: '',
        roleNames: [] as string[],
      };
    },
    /** "ЧЧ:ММ" -> минуты или null если пусто/невалидно */
    onNewTestDurationInput(value: unknown): void {
      (this.newTest as TestFormVM).durationForm = value !== null && value !== undefined ? String(value) : '';
    },
    onEditTestDurationInput(value: unknown): void {
      (this.editTest as TestFormVM).durationForm = value !== null && value !== undefined ? String(value) : '';
    },
    parseDurationForm(s: string): number | null {
      const t = (s || '').trim();
      if (!t) return null;
      const parts = t.split(':');
      const h = parseInt(parts[0], 10) || 0;
      const m = parseInt(parts[1], 10) || 0;
      if (h < 0 || m < 0 || m > 59) return null;
      return h * 60 + m;
    },
    /** минуты -> "ЧЧ:ММ" */
    durationMinutesToForm(minutes: number | null | undefined): string {
      if (minutes == null || minutes === 0) return '';
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    },
    formatDurationDisplay(minutes: number | null | undefined): string {
      if (minutes == null) return 'без ограничения';
      return this.durationMinutesToForm(minutes);
    },
    async saveEditTest() {
      if (this.editTestId == null || !this.editTest.name?.trim()) return;
      this.saving = true;
      try {
        await this.adminService.updateTest(this.editTestId, {
          name: this.editTest.name.trim(),
          description: this.editTest.description?.trim() || undefined,
          passingThreshold: this.editTest.passingThreshold ?? 80,
          maxAttempts: this.editTest.maxAttempts ?? undefined,
          durationMinutes: this.parseDurationForm(this.editTest.durationForm),
          roleNames: this.editTest.roleNames ?? [],
        });
        const toast = await toastController.create({
          message: 'Тест сохранён',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
        this.closeEditModal();
        await this.loadTests();
      } catch (e) {
        console.error('updateTest', e);
        const toast = await toastController.create({
          message: 'Ошибка сохранения',
          duration: 2000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.saving = false;
      }
    },
    async openQuestionsModal(t: IAdminTest) {
      this.questionsTestId = t.id;
      this.questionsTestName = t.name;
      this.showQuestionsModal = true;
      this.questionsList = [];
      this.showAddQuestionForm = false;
      this.addAnswerForQid = null;
      this.editQuestionId = null;
      this.editAnswerId = null;
      this.questionsLoading = true;
      try {
        this.questionsList = await this.adminService.getTestQuestions(t.id);
      } catch (e) {
        console.error('getTestQuestions', e);
        const toast = await toastController.create({
          message: 'Ошибка загрузки вопросов',
          duration: 2000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.questionsLoading = false;
      }
    },
    closeQuestionsModal() {
      this.showQuestionsModal = false;
      this.questionsTestId = null;
      this.questionsTestName = '';
      this.questionsList = [];
      this.showAddQuestionForm = false;
      this.newQuestion = { text: '', multipleChoice: false, sortOrder: 0 };
      this.addAnswerForQid = null;
      this.newAnswer = { text: '', isCorrect: false };
      this.editQuestionId = null;
      this.editAnswerId = null;
    },
    cancelAddQuestion() {
      this.showAddQuestionForm = false;
      this.newQuestion = { text: '', multipleChoice: false, sortOrder: 0 };
    },
    async saveNewQuestion() {
      if (this.questionsTestId == null || !this.newQuestion.text?.trim()) return;
      this.saving = true;
      try {
        const created = await this.adminService.createQuestion(this.questionsTestId, {
          text: this.newQuestion.text.trim(),
          multipleChoice: this.newQuestion.multipleChoice,
          sortOrder: this.newQuestion.sortOrder ?? 0,
        });
        this.questionsList = [...this.questionsList, { ...created, answers: [] }];
        this.newQuestion = { text: '', multipleChoice: false, sortOrder: this.questionsList.length };
        const toast = await toastController.create({
          message: 'Вопрос добавлен',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
        this.showAddQuestionForm = false;
      } catch (e) {
        console.error('createQuestion', e);
        const toast = await toastController.create({
          message: 'Ошибка добавления вопроса',
          duration: 2000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.saving = false;
      }
    },
    startEditQuestion(q: IAdminQuestion) {
      this.editQuestionId = q.id;
      this.editQuestionForm = {
        text: q.text ?? '',
        multipleChoice: !!q.multipleChoice,
        sortOrder: q.sortOrder ?? 0,
      };
    },
    async saveEditQuestion() {
      if (this.questionsTestId == null || this.editQuestionId == null) return;
      this.saving = true;
      try {
        await this.adminService.updateQuestion(this.questionsTestId, this.editQuestionId, {
          text: this.editQuestionForm.text.trim(),
          multipleChoice: this.editQuestionForm.multipleChoice,
          sortOrder: this.editQuestionForm.sortOrder ?? 0,
        });
        const idx = this.questionsList.findIndex((x) => x.id === this.editQuestionId);
        if (idx >= 0) {
          const q = this.questionsList[idx];
          this.questionsList = [
            ...this.questionsList.slice(0, idx),
            { ...q, text: this.editQuestionForm.text, multipleChoice: this.editQuestionForm.multipleChoice, sortOrder: this.editQuestionForm.sortOrder },
            ...this.questionsList.slice(idx + 1),
          ];
        }
        this.editQuestionId = null;
        const toast = await toastController.create({
          message: 'Вопрос сохранён',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
      } catch (e) {
        console.error('updateQuestion', e);
        const toast = await toastController.create({
          message: 'Ошибка сохранения',
          duration: 2000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.saving = false;
      }
    },
    async deleteQuestionConfirm(q: IAdminQuestion) {
      if (this.questionsTestId == null) return;
      const alert = await alertController.create({
        header: 'Удалить вопрос?',
        message: q.text ? `«${q.text.substring(0, 50)}${q.text.length > 50 ? '…' : ''}»` : 'Вопрос будет удалён.',
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          {
            text: 'Удалить',
            role: 'destructive',
            handler: async () => {
              try {
                const testId = this.questionsTestId;
                if (testId == null) return;
                await this.adminService.deleteQuestion(testId, q.id);
                this.questionsList = this.questionsList.filter((x) => x.id !== q.id);
                const toast = await toastController.create({
                  message: 'Вопрос удалён',
                  duration: 2000,
                  position: 'top',
                  color: 'success',
                });
                await toast.present();
              } catch (e) {
                console.error('deleteQuestion', e);
                const toast = await toastController.create({
                  message: 'Ошибка удаления',
                  duration: 2000,
                  position: 'top',
                  color: 'danger',
                });
                await toast.present();
              }
            },
          },
        ],
      });
      await alert.present();
    },
    startAddAnswer(q: IAdminQuestion) {
      this.addAnswerForQid = q.id;
      this.newAnswer = { text: '', isCorrect: false };
    },
    cancelAddAnswer() {
      this.addAnswerForQid = null;
      this.newAnswer = { text: '', isCorrect: false };
    },
    async saveNewAnswer(qid: number) {
      if (!this.newAnswer.text?.trim()) return;
      this.saving = true;
      try {
        const created = await this.adminService.createAnswer(qid, {
          text: this.newAnswer.text.trim(),
          isCorrect: this.newAnswer.isCorrect,
        });
        const qIdx = this.questionsList.findIndex((x) => x.id === qid);
        if (qIdx >= 0) {
          const q = this.questionsList[qIdx];
          const answers = [...(q.answers || []), created];
          this.questionsList = [...this.questionsList.slice(0, qIdx), { ...q, answers }, ...this.questionsList.slice(qIdx + 1)];
        }
        this.addAnswerForQid = null;
        this.newAnswer = { text: '', isCorrect: false };
        const toast = await toastController.create({
          message: 'Ответ добавлен',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
      } catch (e) {
        console.error('createAnswer', e);
        const toast = await toastController.create({
          message: 'Ошибка добавления ответа',
          duration: 2000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.saving = false;
      }
    },
    startEditAnswer(ans: IAdminAnswer) {
      this.editAnswerId = ans.id;
      this.editAnswerForm = { text: ans.text ?? '', isCorrect: !!ans.isCorrect };
    },
    async saveEditAnswer() {
      if (this.editAnswerId == null) return;
      this.saving = true;
      try {
        await this.adminService.updateAnswer(this.editAnswerId, {
          text: this.editAnswerForm.text.trim(),
          isCorrect: this.editAnswerForm.isCorrect,
        });
        this.questionsList = this.questionsList.map((q) => ({
          ...q,
          answers: (q.answers || []).map((a) =>
            a.id === this.editAnswerId
              ? { ...a, text: this.editAnswerForm.text, isCorrect: this.editAnswerForm.isCorrect }
              : a
          ),
        }));
        this.editAnswerId = null;
        const toast = await toastController.create({
          message: 'Ответ сохранён',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
      } catch (e) {
        console.error('updateAnswer', e);
        const toast = await toastController.create({
          message: 'Ошибка сохранения',
          duration: 2000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.saving = false;
      }
    },
    async deleteAnswerConfirm(ans: IAdminAnswer) {
      const alert = await alertController.create({
        header: 'Удалить ответ?',
        message: ans.text ? `«${ans.text.substring(0, 50)}${ans.text.length > 50 ? '…' : ''}»` : 'Ответ будет удалён.',
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          {
            text: 'Удалить',
            role: 'destructive',
            handler: async () => {
              try {
                await this.adminService.deleteAnswer(ans.id);
                this.questionsList = this.questionsList.map((q) => ({
                  ...q,
                  answers: (q.answers || []).filter((a) => a.id !== ans.id),
                }));
                const toast = await toastController.create({
                  message: 'Ответ удалён',
                  duration: 2000,
                  position: 'top',
                  color: 'success',
                });
                await toast.present();
              } catch (e) {
                console.error('deleteAnswer', e);
                const toast = await toastController.create({
                  message: 'Ошибка удаления',
                  duration: 2000,
                  position: 'top',
                  color: 'danger',
                });
                await toast.present();
              }
            },
          },
        ],
      });
      await alert.present();
    },
    toggleUserPick(userId: number) {
      const idx = this.selectedUserIds.indexOf(userId);
      if (idx >= 0) this.selectedUserIds.splice(idx, 1);
      else this.selectedUserIds.push(userId);
    },
    async confirmAddUsers() {
      if (this.addUsersTestId == null || !this.selectedUserIds.length) return;
      this.addingUsers = true;
      try {
        await this.adminService.addTestAssignments(this.addUsersTestId, [...this.selectedUserIds]);
        this.expandedTestId = this.addUsersTestId;
        this.currentAssignments = await this.adminService.getTestAssignments(this.addUsersTestId);
        const toast = await toastController.create({
          message: 'Пользователи добавлены',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
        this.closeAddUsersModal();
      } catch (e) {
        console.error('addTestAssignments', e);
        const toast = await toastController.create({
          message: 'Ошибка добавления',
          duration: 2000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.addingUsers = false;
      }
    },
    async deleteTestConfirm(t: IAdminTest) {
      const alert = await alertController.create({
        header: 'Удалить тест?',
        message: `Тест «${t.name}» будет удалён вместе с вопросами и назначениями.`,
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          {
            text: 'Удалить',
            role: 'destructive',
            handler: async () => {
              try {
                await this.adminService.deleteTest(t.id);
                if (this.expandedTestId === t.id) this.expandedTestId = null;
                await this.loadTests();
                const toast = await toastController.create({
                  message: 'Тест удалён',
                  duration: 2000,
                  position: 'top',
                  color: 'success',
                });
                await toast.present();
              } catch (e) {
                console.error('deleteTest', e);
                const toast = await toastController.create({
                  message: 'Ошибка удаления',
                  duration: 2000,
                  position: 'top',
                  color: 'danger',
                });
                await toast.present();
              }
            },
          },
        ],
      });
      await alert.present();
    },
  },
});
</script>

<style scoped>
.admin-card {
  margin: 16px;
  --background: var(--ion-color-dark);
}

.admin-card ion-card-title {
  color: var(--ion-text-color);
}

.admin-card ion-card-content {
  color: var(--ion-text-color);
  opacity: 0.9;
}

.admin-card ion-item {
  --background: transparent;
  --color: var(--ion-text-color);
}

.admin-hint {
  color: var(--ion-text-color);
  opacity: 0.8;
  padding: 12px 0;
}

.admin-tests-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.admin-test-row {
  border: 1px solid var(--ion-border-color);
  border-radius: 8px;
  overflow: hidden;
}

.admin-test-main {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  flex-wrap: wrap;
}

@media (max-width: 768px) {
  .admin-test-main {
    flex-direction: column;
    align-items: stretch;
  }
  .admin-test-main ion-label {
    flex: none;
  }
  .admin-test-main ion-button {
    align-self: flex-start;
  }
  .admin-test-assignments {
    padding: 10px 12px;
  }
  .admin-assignments-list {
    margin: 0 -12px;
  }
  .admin-assignment-item {
    min-height: auto;
    --padding-start: 12px;
    --padding-end: 12px;
    --inner-padding-end: 12px;
    padding-top: 10px;
    padding-bottom: 10px;
    align-items: flex-start;
  }
  .admin-assignment-item ion-label {
    white-space: normal;
  }
  .admin-assignment-item .admin-assignment-actions {
    margin-top: 10px;
    padding-top: 8px;
    border-top: 1px solid var(--ion-border-color);
  }
  .admin-assignment-status-row {
    flex-wrap: wrap;
    gap: 6px;
  }
  .admin-assignment-status,
  .admin-assignment-attempts,
  .admin-assignment-score {
    font-size: 0.7rem;
  }
  .admin-assignment-item ion-label h3 {
    font-size: 0.95rem;
    word-break: break-word;
  }
  .admin-assignment-login {
    font-size: 0.8rem;
  }
  .admin-assignments-search {
    margin: 0 -4px;
  }
}

.admin-test-main ion-label {
  flex: 1;
  min-width: 0;
}

.admin-test-main ion-label h2 {
  color: var(--ion-text-color);
}

.admin-test-main ion-label p {
  color: var(--ion-text-color);
  opacity: 0.8;
  font-size: 0.85rem;
}

.admin-test-assignments {
  padding: 12px 16px;
  border-top: 1px solid var(--ion-border-color);
  background: rgba(0, 0, 0, 0.2);
}

.admin-assignments-title {
  margin: 0 0 8px;
  font-weight: 600;
  color: var(--ion-text-color);
}

.admin-assignments-list,
.admin-assignments-list ion-item {
  --background: transparent !important;
  --background-activated: transparent !important;
  --background-hover: transparent !important;
  --background-focused: transparent !important;
}
.admin-assignments-list {
  background: transparent !important;
}
.admin-assignments-list ion-item::part(native),
.admin-assignment-item::part(native) {
  --background: transparent !important;
  background: transparent !important;
}
.admin-assignment-item {
  --background: transparent !important;
  --background-activated: transparent !important;
  --background-hover: transparent !important;
  --background-focused: transparent !important;
  --color: var(--ion-text-color);
}

.admin-assignment-login {
  margin: 0 0 6px;
  font-size: 0.85rem;
  opacity: 0.9;
}

.admin-assignments-search {
  --background: transparent;
  padding: 8px 0;
}

.admin-assignment-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  margin-top: 8px;
}

.admin-assignment-item ion-label h3 {
  margin: 0 0 6px;
  font-weight: 600;
}

.admin-assignment-status-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  font-size: 0.85rem;
}

.admin-assignment-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.75rem;
}

.admin-assignment-status ion-icon {
  font-size: 0.95rem;
}

.admin-assignment-status--passed {
  background: var(--ion-color-success);
  color: var(--ion-color-success-contrast);
}

.admin-assignment-status--draft {
  background: var(--ion-color-warning);
  color: var(--ion-color-warning-contrast);
}

.admin-assignment-status--failed {
  background: var(--ion-color-danger);
  color: var(--ion-color-danger-contrast);
}

.admin-assignment-status--none {
  background: var(--ion-color-medium);
  color: var(--ion-color-medium-contrast);
}

.admin-assignment-attempts,
.admin-assignment-score {
  color: var(--ion-text-color);
  opacity: 0.9;
}

/* Кнопки в стиле приложения (оранжевые, как в других разделах) */
.admin-primary-btn,
.admin-primary-btn.ion-activated,
.admin-primary-btn.ion-focused {
  --background: var(--ion-color-orange);
  --background-activated: var(--ion-color-orange-tint);
  --background-hover: var(--ion-color-orange-tint);
  --background-focused: var(--ion-color-orange-tint);
  --color: #fff;
}

.admin-outline-btn,
.admin-outline-btn.ion-activated,
.admin-outline-btn.ion-focused {
  --border-color: var(--ion-color-orange);
  --color: var(--ion-color-orange);
  --color-activated: var(--ion-color-orange-tint);
  --color-hover: var(--ion-color-orange-tint);
}

.admin-card .admin-primary-btn,
.admin-card .admin-outline-btn {
  margin-top: 12px;
  margin-bottom: 4px;
}

.admin-card .admin-outline-btn {
  margin-top: 8px;
}

.admin-card-btn {
  --color: var(--ion-text-color);
  margin: 0 2px;
}

.admin-test-assignments .admin-card-btn {
  min-height: 32px;
}

.admin-modal-actions {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.admin-modal-actions .admin-primary-btn {
  margin-top: 0;
}

.admin-modal-actions .admin-outline-btn {
  margin-top: 0;
}

.admin-users-modal ion-list,
.admin-users-modal ion-item,
.admin-users-modal .admin-user-pick-item {
  --background: transparent !important;
  --background-activated: transparent !important;
  --background-hover: transparent !important;
  --background-focused: transparent !important;
  --color: var(--ion-text-color);
}
.admin-users-modal ion-list {
  background: transparent !important;
}
.admin-users-modal ion-item::part(native),
.admin-users-modal .admin-user-pick-item::part(native) {
  --background: transparent !important;
  background: transparent !important;
}

.admin-users-modal .admin-user-pick-item ion-label h3,
.admin-users-modal .admin-user-pick-item ion-label p {
  color: var(--ion-text-color);
}

/* Чекбоксы в модалке «Добавить пользователей» */
.admin-users-modal ion-checkbox {
  --size: 22px;
  --checkbox-background: transparent;
  --checkbox-background-checked: var(--ion-color-orange);
  --border-color: rgba(255, 255, 255, 0.5);
  --border-color-checked: var(--ion-color-orange);
  --checkmark-color: #fff;
  margin-right: 8px;
}
.admin-users-modal ion-checkbox::part(container) {
  border-radius: 4px;
  border-width: 2px;
}

.admin-users-modal-footer .admin-modal-actions {
  margin-top: 0;
  padding: 12px 16px;
  padding-bottom: max(12px, env(safe-area-inset-bottom));
}
</style>

<!-- Стили без scoped для модалки: контент рендерится вне компонента, кнопки должны быть оранжевыми -->
<style>
.admin-users-modal ion-list,
.admin-users-modal ion-item,
.admin-users-modal .admin-user-pick-item {
  --background: transparent !important;
  --background-activated: transparent !important;
  --background-hover: transparent !important;
  --background-focused: transparent !important;
}
.admin-users-modal ion-list {
  background: transparent !important;
}
.admin-users-modal ion-item::part(native),
.admin-users-modal .admin-user-pick-item::part(native) {
  --background: transparent !important;
  background: transparent !important;
}
.admin-users-modal ion-checkbox {
  --size: 22px;
  --checkbox-background: transparent;
  --checkbox-background-checked: var(--ion-color-orange) !important;
  --border-color: rgba(255, 255, 255, 0.5);
  --border-color-checked: var(--ion-color-orange) !important;
  --checkmark-color: #fff !important;
}
.admin-users-modal ion-checkbox::part(container) {
  border-radius: 4px;
  border-width: 2px;
}
.admin-users-modal .admin-primary-btn,
.admin-users-modal .admin-primary-btn.ion-activated {
  --background: var(--ion-color-orange) !important;
  --background-activated: var(--ion-color-orange-tint) !important;
  --color: #fff !important;
}
.admin-users-modal .admin-outline-btn,
.admin-users-modal .admin-outline-btn.ion-activated {
  --border-color: var(--ion-color-orange) !important;
  --color: var(--ion-color-orange) !important;
}
.admin-users-modal ion-buttons ion-button {
  --color: var(--ion-color-orange) !important;
}
/* Кнопка закрытия в хедере модалки — тёмная, чтобы не сливаться с белым хедером на светлой теме */
.admin-users-modal ion-header ion-toolbar ion-buttons ion-button,
.admin-users-modal ion-header ion-toolbar ion-buttons ion-button ion-icon {
  --color: var(--ion-color-step-850, #333) !important;
  color: var(--ion-color-step-850, #333) !important;
}

/* Модалка редактирования теста — оранжевые кнопки */
.admin-edit-test-modal .admin-primary-btn,
.admin-edit-test-modal .admin-primary-btn.ion-activated {
  --background: var(--ion-color-orange) !important;
  --background-activated: var(--ion-color-orange-tint) !important;
  --color: #fff !important;
}
.admin-edit-test-modal .admin-outline-btn,
.admin-edit-test-modal .admin-outline-btn.ion-activated {
  --border-color: var(--ion-color-orange) !important;
  --color: var(--ion-color-orange) !important;
}
.admin-edit-test-modal ion-buttons ion-button {
  --color: var(--ion-color-orange) !important;
}
.admin-edit-test-modal ion-header ion-toolbar ion-buttons ion-button,
.admin-edit-test-modal ion-header ion-toolbar ion-buttons ion-button ion-icon {
  --color: var(--ion-color-step-850, #333) !important;
  color: var(--ion-color-step-850, #333) !important;
}
.admin-edit-test-modal .admin-card ion-item,
.admin-edit-test-modal ion-item {
  --background: transparent;
  --color: var(--ion-text-color);
}

/* Модалка вопросов и ответов */
.admin-questions-list {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.admin-question-block {
  border: 1px solid var(--ion-border-color);
  border-radius: 8px;
  padding: 12px;
  background: rgba(0, 0, 0, 0.15);
}

.admin-question-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: wrap;
}

.admin-question-title {
  flex: 1;
  min-width: 0;
  color: var(--ion-text-color);
}

.admin-question-title strong {
  display: block;
  margin-bottom: 4px;
}

.admin-badge {
  display: inline-block;
  font-size: 0.75rem;
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--ion-color-medium);
  color: var(--ion-color-light);
  margin-right: 8px;
}

.admin-badge.correct {
  background: var(--ion-color-success);
}

.admin-question-order {
  font-size: 0.8rem;
  opacity: 0.8;
  color: var(--ion-text-color);
}

.admin-question-actions {
  display: flex;
  gap: 4px;
}

.admin-answers-list {
  list-style: none;
  padding: 0;
  margin: 8px 0 0;
}

.admin-answer-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 6px 0;
  border-bottom: 1px solid var(--ion-border-color);
  color: var(--ion-text-color);
}

.admin-answer-row:last-child {
  border-bottom: none;
}

.admin-answer-text {
  flex: 1;
  min-width: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.admin-answer-input {
  max-width: 200px;
  --padding-start: 8px;
  --padding-end: 8px;
}

/* Мобильная верстка модалки «Вопросы и ответы» */
@media (max-width: 768px) {
  .admin-questions-modal .admin-question-block {
    padding: 10px;
  }
  .admin-questions-modal .admin-question-header {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
  .admin-questions-modal .admin-question-title {
    flex: none;
  }
  .admin-questions-modal .admin-question-actions {
    flex-wrap: wrap;
  }
  .admin-questions-modal .admin-answer-row {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    padding: 10px 0;
  }
  .admin-questions-modal .admin-answer-text {
    flex: none;
    width: 100%;
    min-width: 0;
    line-height: 1.4;
    padding-right: 0;
  }
  .admin-questions-modal .admin-answer-row .admin-badge {
    align-self: flex-start;
  }
  .admin-questions-modal .admin-answer-row ion-button {
    align-self: flex-start;
  }
  .admin-questions-modal .admin-answer-input {
    max-width: none;
    width: 100%;
  }
  .admin-questions-modal .admin-add-answer .admin-answer-input,
  .admin-questions-modal .admin-add-answer ion-checkbox,
  .admin-questions-modal .admin-add-answer ion-button {
    width: 100%;
    max-width: none;
  }
  .admin-questions-modal .admin-edit-inline ion-input,
  .admin-questions-modal .admin-edit-inline ion-button {
    width: 100%;
    max-width: none;
  }
}

.admin-add-answer-btn {
  margin-top: 8px;
}

.admin-question-form.admin-card {
  margin: 0 0 16px;
  padding: 12px;
}

.admin-question-form ion-item,
.admin-edit-inline ion-item {
  --background: transparent;
  --color: var(--ion-text-color);
}

.admin-edit-inline {
  margin-top: 8px;
  padding: 8px;
  border: 1px dashed var(--ion-border-color);
  border-radius: 8px;
}

.admin-add-answer {
  border: 1px dashed var(--ion-border-color);
  border-radius: 8px;
  padding: 8px;
  margin-top: 4px;
}
</style>

<!-- Модалка вопросов — оранжевые кнопки в стиле приложения (рендерится вне компонента) -->
<style>
.admin-questions-modal .admin-primary-btn,
.admin-questions-modal .admin-primary-btn.ion-activated {
  --background: var(--ion-color-orange) !important;
  --background-activated: var(--ion-color-orange-tint) !important;
  --color: #fff !important;
}
.admin-questions-modal .admin-outline-btn,
.admin-questions-modal .admin-outline-btn.ion-activated,
.admin-questions-modal .admin-add-question-btn,
.admin-questions-modal .admin-add-question-btn.ion-activated {
  --border-color: var(--ion-color-orange) !important;
  --color: var(--ion-color-orange) !important;
}
.admin-questions-modal ion-buttons ion-button {
  --color: var(--ion-color-orange) !important;
}
/* Чекбокс и кнопки в строке редактирования ответа */
.admin-questions-modal .admin-answer-row ion-checkbox.admin-answer-checkbox,
.admin-questions-modal .admin-answer-row ion-checkbox {
  --size: 22px;
  --checkbox-background-checked: var(--ion-color-orange) !important;
  --checkmark-color: #fff !important;
  --border-color: var(--ion-color-medium);
  --border-color-checked: var(--ion-color-orange) !important;
  margin-right: 8px;
}
.admin-questions-modal .admin-answer-row ion-checkbox.admin-answer-checkbox::part(container),
.admin-questions-modal .admin-answer-row ion-checkbox::part(container) {
  border-radius: 4px;
}
.admin-questions-modal .admin-answer-row .admin-primary-btn {
  margin-right: 4px;
}
.admin-questions-modal ion-header ion-toolbar ion-buttons ion-button,
.admin-questions-modal ion-header ion-toolbar ion-buttons ion-button ion-icon {
  --color: var(--ion-color-step-850, #333) !important;
  color: var(--ion-color-step-850, #333) !important;
}
.admin-questions-modal ion-item {
  --background: transparent;
  --color: var(--ion-text-color);
}
.admin-questions-content {
  padding-bottom: max(24px, env(safe-area-inset-bottom));
}
.admin-questions-modal .admin-answer-input,
.admin-questions-modal .admin-add-answer .admin-answer-input {
  margin-left: 12px;
  margin-right: 12px;
  width: calc(100% - 24px);
  max-width: calc(100% - 24px);
}
.admin-questions-modal .admin-add-answer,
.admin-questions-modal .admin-edit-inline {
  padding-left: 12px;
  padding-right: 12px;
}
</style>

<style>
/* Модалка вопросов — оранжевые кнопки (контент рендерится вне компонента) */
.admin-questions-modal .admin-primary-btn,
.admin-questions-modal .admin-primary-btn.ion-activated {
  --background: var(--ion-color-orange) !important;
  --background-activated: var(--ion-color-orange-tint) !important;
  --color: #fff !important;
}
.admin-questions-modal .admin-outline-btn,
.admin-questions-modal .admin-outline-btn.ion-activated {
  --border-color: var(--ion-color-orange) !important;
  --color: var(--ion-color-orange) !important;
}
.admin-questions-modal ion-buttons ion-button {
  --color: var(--ion-color-orange) !important;
}
.admin-questions-modal ion-header ion-toolbar ion-buttons ion-button,
.admin-questions-modal ion-header ion-toolbar ion-buttons ion-button ion-icon {
  --color: var(--ion-color-step-850, #333) !important;
  color: var(--ion-color-step-850, #333) !important;
}
.admin-questions-modal ion-item {
  --background: transparent;
  --color: var(--ion-text-color);
}
</style>
